import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { colors, spacing, radius, shadow, typography } from '../theme';

/* ─── Divider ────────────────────────────────────────── */
export function Divider({ style }) {
  return <View style={[s.divider, style]} />;
}

/* ─── Section Header ─────────────────────────────────── */
export function SectionTitle({ label, right, rightOnPress }) {
  return (
    <View style={s.sectionHead}>
      <Text style={s.sectionLabel}>{label}</Text>
      {right && (
        <TouchableOpacity onPress={rightOnPress}>
          <Text style={s.sectionRight}>{right}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

/* ─── Primary Button ─────────────────────────────────── */
export function CTAButton({ label, onPress, style, loading, variant = 'primary' }) {
  const isPrimary = variant === 'primary';
  return (
    <TouchableOpacity
      style={[s.cta, isPrimary ? s.ctaPrimary : s.ctaOutline, style]}
      onPress={onPress}
      activeOpacity={0.82}
      disabled={loading}
    >
      {loading
        ? <ActivityIndicator color={isPrimary ? '#fff' : colors.accent.main} size="small" />
        : <Text style={[s.ctaText, !isPrimary && s.ctaTextOutline]}>{label}</Text>
      }
    </TouchableOpacity>
  );
}

/* ─── Score Bar ──────────────────────────────────────── */
export function ScoreBar({ value, style }) {
  const pct = Math.min(100, Math.max(0, value));
  const barColor = pct >= 90 ? colors.status.success
    : pct >= 75 ? colors.accent.main
    : colors.text.faint;
  return (
    <View style={[s.barTrack, style]}>
      <View style={[s.barFill, { width: `${pct}%`, backgroundColor: barColor }]} />
    </View>
  );
}

/* ─── Item Tile ──────────────────────────────────────── */
export function ItemTile({ emoji, label, owned, size = 'md', imageUri }) {
  const dim = size === 'lg'
    ? { w: 70, h: 84, font: 28 }
    : { w: 52, h: 62, font: 22 };
  return (
    <View style={s.tileCol}>
      <View style={[
        s.tileBox,
        { width: dim.w, height: dim.h },
        owned && s.tileOwned,
      ]}>
        <Text style={{ fontSize: dim.font }}>{emoji}</Text>
        {owned && <View style={s.ownedBadge}><Text style={s.ownedBadgeText}>✓</Text></View>}
      </View>
      {label && <Text style={[s.tileLabel, owned && s.tileLabelOwned]}>{label}</Text>}
    </View>
  );
}

/* ─── AI Tip ─────────────────────────────────────────── */
export function AITip({ text }) {
  return (
    <View style={s.aiStrip}>
      <View style={s.aiChip}>
        <Text style={s.aiChipText}>IA</Text>
      </View>
      <Text style={s.aiText}>{text}</Text>
    </View>
  );
}

/* ─── Badge ──────────────────────────────────────────── */
export function Badge({ label, color = 'accent' }) {
  const bg = color === 'accent' ? colors.accent.ghost
    : color === 'success' ? '#E8F4ED'
    : color === 'error' ? '#FAEAEA'
    : colors.bg.surface;
  const fg = color === 'accent' ? colors.accent.dark
    : color === 'success' ? colors.status.success
    : color === 'error' ? colors.status.error
    : colors.text.secondary;
  return (
    <View style={[s.badge, { backgroundColor: bg }]}>
      <Text style={[s.badgeText, { color: fg }]}>{label}</Text>
    </View>
  );
}

/* ─── Partner Image Card ─────────────────────────────── */
export function ProductCard({ emoji, name, brand, price, partnerColor, onPress }) {
  return (
    <TouchableOpacity style={s.productCard} onPress={onPress} activeOpacity={0.85}>
      <View style={[s.productImg, { backgroundColor: colors.bg.surface }]}>
        <Text style={s.productEmoji}>{emoji}</Text>
        {partnerColor && <View style={[s.partnerDot, { backgroundColor: partnerColor }]} />}
      </View>
      <Text style={s.productName} numberOfLines={2}>{name}</Text>
      <Text style={s.productBrand}>{brand}</Text>
      <Text style={s.productPrice}>{price} €</Text>
    </TouchableOpacity>
  );
}

/* ─── StatusBar mock ─────────────────────────────────── */
export function StatusBar() { return null; }

/* ─── GoldDivider (legacy compat) ───────────────────── */
export function GoldDivider({ style }) {
  return <View style={[s.goldDiv, style]} />;
}

const s = StyleSheet.create({
  divider:       { height: 1, backgroundColor: colors.bg.border },
  goldDiv:       { height: 1, backgroundColor: colors.bg.border, marginBottom: spacing.sm },
  sectionHead:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  sectionLabel:  { fontSize: 9, letterSpacing: 3, fontWeight: '500', color: colors.text.faint, textTransform: 'uppercase' },
  sectionRight:  { fontSize: 10, letterSpacing: 1.5, color: colors.accent.main, textTransform: 'uppercase' },

  cta:           { paddingVertical: 15, alignItems: 'center', borderRadius: radius.sm, marginHorizontal: spacing.xl },
  ctaPrimary:    { backgroundColor: colors.text.primary },
  ctaOutline:    { backgroundColor: 'transparent', borderWidth: 1.5, borderColor: colors.text.primary },
  ctaText:       { fontSize: 11, letterSpacing: 3, fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', color: '#FFFFFF' },
  ctaTextOutline:{ fontSize: 11, letterSpacing: 3, fontWeight: '600', color: '#FFFFFF', textTransform: 'uppercase', color: colors.text.primary },

  barTrack:      { height: 3, backgroundColor: colors.bg.border, borderRadius: 2, overflow: 'hidden' },
  barFill:       { height: '100%', borderRadius: 2 },

  tileCol:       { alignItems: 'center', gap: 5 },
  tileBox:       { backgroundColor: colors.bg.surface, borderRadius: radius.sm, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.bg.border },
  tileOwned:     { backgroundColor: colors.accent.ghost, borderColor: colors.accent.pale },
  ownedBadge:    { position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: 8, backgroundColor: colors.status.success, alignItems: 'center', justifyContent: 'center' },
  ownedBadgeText:{ fontSize: 8, color: '#fff', fontWeight: '700' },
  tileLabel:     { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase' },
  tileLabelOwned:{ color: colors.accent.main },

  aiStrip:       { flexDirection: 'row', gap: 10, alignItems: 'flex-start', backgroundColor: colors.accent.ghost, borderRadius: radius.md, padding: spacing.md, marginHorizontal: spacing.xl, marginBottom: spacing.md, borderWidth: 1, borderColor: colors.accent.pale },
  aiChip:        { width: 26, height: 26, borderRadius: 8, backgroundColor: colors.accent.main, alignItems: 'center', justifyContent: 'center' },
  aiChipText:    { fontSize: 8, letterSpacing: 1, color: '#fff', fontWeight: '700' },
  aiText:        { flex: 1, fontSize: 12, color: colors.text.secondary, fontStyle: 'italic', lineHeight: 18, letterSpacing: 0.2 },

  badge:         { paddingHorizontal: 9, paddingVertical: 3, borderRadius: radius.pill },
  badgeText:     { fontSize: 9, letterSpacing: 1.5, fontWeight: '600', textTransform: 'uppercase' },

  productCard:   { width: 150, marginRight: spacing.md },
  productImg:    { width: 150, height: 180, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm, position: 'relative' },
  productEmoji:  { fontSize: 52 },
  partnerDot:    { position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: 4 },
  productName:   { fontSize: 12, color: colors.text.primary, letterSpacing: 0.2, lineHeight: 17, marginBottom: 2 },
  productBrand:  { fontSize: 10, color: colors.text.secondary, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 },
  productPrice:  { fontSize: 14, fontWeight: '600', color: colors.text.primary },
});
